import { Auth } from '@aws-amplify/auth'
import { load as WebFontLoader } from 'webfontloader'
import { registerManifest } from 'featureSliced/shared/utils/registerManifest'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import { setFont } from 'featureSliced/shared/utils/setFont'
import { AppDispatch, RootState } from '../../index'
import { AppSlice } from '.'

import {
  darkTheme,
  ICompanyData,
  IInterface,
  initialState,
  lightTheme,
} from './types'
import { AuthSlice } from '../AuthReducer'
import { CommonSlice } from '../CommonReducer'
import { handleLocalStorage } from 'featureSliced/shared/utils/handleLocalStorage'
import {
  normalizeAllowedLanguagesFromSetup,
  resolveLanguageAfterSetup,
} from 'featureSliced/shared/utils/localization/setupLanguages'
import * as Sentry from '@sentry/react'

const preloadImage = (url: string) => {
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = url
  link.type = 'image/png'
  document.head.appendChild(link)
}

export const getAppData =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const [setup, companyData] = await Promise.all([
      qrMenuGET.setup(),
      qrMenuGET.companyData(),
    ])

    Sentry.setTags({
      QRMenuClientId: setup?.address?.QRMenuClientId,
      companyName: companyData?.CompanyName,
      companyId: companyData?.CompanyID,
    })

    const appName =
      process.env.REACT_APP_IS_CLIC === '0' ? 'QRMENU' : 'CLICKCOLLECT'

    const imagesToPreload = [
      setup?.interfaceConfig?.[appName]?.appIcon,
      setup?.interfaceConfig?.[appName]?.logoImage,
      setup?.interfaceConfig?.[appName]?.backgroundImage,
    ].filter(Boolean)

    imagesToPreload.forEach(preloadImage)

    const appFont = setup?.interfaceConfig?.[appName]?.font
    const reservationFont = setup?.tableReservationConfig?.font
    const fontsToLoad = [
      ...new Set([appFont, reservationFont].filter(Boolean)),
    ] as string[]
    if (fontsToLoad.length) {
      WebFontLoader({
        google: {
          families: fontsToLoad,
        },
      })
    }
    if (appFont) {
      setFont(appFont)
    }

    registerManifest(
      companyData?.ContractorFirma ||
        companyData?.CompanyName ||
        companyData?.ContractorName ||
        '',
      setup?.interfaceConfig?.[appName]?.appIcon ||
        setup?.interfaceConfig?.[appName]?.logoImage
    )
    if (!setup?.menuConfig?.[appName]?.loginAvailable) {
      Auth.signOut()
      dispatch(AuthSlice.actions.signOut())
      window.localStorage.removeItem('userName')
    }

    const interfaceStyles = getInterfaceStyles({
      ...initialState.interfaceStyles,
      ...setup?.interfaceConfig?.[appName],
    })

    // Booking screens follow the admin's "Reservation interface design";
    // a company that never saved one keeps the app styles.
    const trConfig = setup?.tableReservationConfig
    const reservationStyles = trConfig?.appTheme
      ? getInterfaceStyles({
          ...interfaceStyles,
          ...(trConfig.colour ? { colour: trConfig.colour } : {}),
          ...(trConfig.font ? { font: trConfig.font } : {}),
          appTheme: trConfig.appTheme,
        })
      : interfaceStyles

    dispatch(
      AppSlice.actions.fetchAppData({
        isAppInit: true,
        companyData,
        address: setup?.address,
        clickCollectConfig: setup?.clickCollectConfig,
        tableReservationConfig: setup?.tableReservationConfig,
        paymentConfig: setup?.paymentConfig,
        menu: setup?.menuConfig?.[appName],
        interfaceStyles,
        reservationStyles,
      })
    )

    const allowed = normalizeAllowedLanguagesFromSetup(setup?.languagesConfig)
    handleLocalStorage({ allowedLanguages: allowed })
    dispatch(CommonSlice.actions.setAllowedLanguages(allowed))

    const next = resolveLanguageAfterSetup(
      allowed,
      getState().common.selectedLanguage
    )
    if (next !== getState().common.selectedLanguage) {
      dispatch(CommonSlice.actions.setSelectedLanguage(next))
    }
  }

const getInterfaceStyles = (styles: Partial<IInterface>) => {
  switch (styles?.appTheme) {
    case 'dark':
      return {
        ...styles,
        ...darkTheme,
      }
    case 'light':
      return {
        ...styles,
        ...lightTheme,
      }
    default:
      return {
        ...styles,
        ...lightTheme,
      }
  }
}
